import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { advertImgUrl, assetUrl, fetchAdverts, resolveAdLink, adForSpace } from "@/lib/glossyApi";
import { ArrowUpRight } from "lucide-react";

// A CONTAINED, clearly-an-advert unit — deliberately smaller than the editorial/feature sections,
// with its own campaign image, copy and per-member click-through link.
// FIXED space: one member holds it for a full edition. Empty -> the space collapses (nothing shown).
export default function AdBand({ space = "home_band_a", excludeMemberId = null }) {
  const [ad, setAd] = useState(null);

  useEffect(() => {
    fetchAdverts().then((list) => setAd(adForSpace(list, space, excludeMemberId))).catch(() => {});
  }, [excludeMemberId, space]);

  if (!ad) return null;
  const a = ad.advert;
  const logo = assetUrl(ad.logo) || ad.fallback_logo;
  const { href, external } = resolveAdLink(ad);
  const cta = a.cta || "Discover";
  const inner = (
    <div className="group grid grid-cols-1 lg:grid-cols-2 border border-ink/15 bg-ink text-cream overflow-hidden" data-testid="ad-band">
      <div className="relative aspect-[4/5] lg:aspect-auto lg:min-h-[380px] overflow-hidden bg-void ed-frame">
        <img src={advertImgUrl(ad)} alt={ad.business_name} className="absolute inset-0 w-full h-full object-cover ed-grade transition-transform duration-700 group-hover:scale-[1.03]" />
      </div>
      <div className="flex flex-col justify-center px-6 sm:px-10 py-10 sm:py-12 relative">
        <div className="flex items-center justify-between mb-6 pb-4 border-b border-cream/15">
          <span className="font-mono text-[10px] uppercase tracking-[0.35em] text-gloss-gold">Advertisement</span>
          {logo && <img src={logo} alt={ad.business_name} className="h-8 w-auto object-contain opacity-95" data-testid="ad-logo" />}
        </div>
        <h2 className="font-serif text-3xl sm:text-5xl tracking-tight leading-[0.95]">{a.headline}</h2>
        {a.subhead && <p className="font-grotesk text-lg sm:text-xl mt-3 font-light text-gloss-gold">{a.subhead}</p>}
        <p className="font-sans text-base text-cream/70 mt-4 max-w-xl leading-relaxed line-clamp-4">{a.body}</p>
        <span className="inline-flex items-center gap-3 mt-7 w-fit bg-cream text-ink px-7 py-3.5 overline group-hover:bg-gloss-gold transition-colors" data-testid="ad-cta">
          {cta} <ArrowUpRight size={15} />
        </span>
        <p className="font-mono text-[10px] uppercase tracking-widest text-cream/40 mt-6">{ad.business_name} · {ad.category} · {ad.borough} · An S/ME Member</p>
      </div>
    </div>
  );

  return (
    <section className="bg-cream py-10 sm:py-14 px-5 sm:px-8">
      <div className="max-w-[1280px] mx-auto">
        {external
          ? <a href={href} target="_blank" rel="noreferrer" className="block">{inner}</a>
          : <Link to={href} className="block">{inner}</Link>}
      </div>
    </section>
  );
}
